import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Building, Edit, Flag, Package, Plus, Printer, Target, Trash2, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SortableEntryList from './SortableEntryList';
import { Checkbox } from './ui/checkbox';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const IncidentObjectivesModule = ({ theme, exerciseId, exerciseName }) => {
  const [objectivesData, setObjectivesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('core-objectives');

  // Helper function to format timestamp in 24-hour format (e.g., "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  // Form state
  const [formData, setFormData] = useState({
    category: 'core-objectives',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_info: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Categories and their fields
  const categories = {
    'core-objectives': {
      label: 'Core Objectives',
      icon: Target,
      fields: [
        { key: 'overall_safety', label: 'Overall Safety (Protect Lives, Property, Environment)', type: 'textarea', required: true, placeholder: 'enter any or all safety aspects of this  objective as it applies to the protection of lives, property and any environmental concerns' },
        { key: 'effective_operations', label: 'Effective and Efficient Operations', type: 'textarea', required: true, placeholder: 'enter Effective and efficient operations under the Incident Command System (ICS) that achieve clear, specific objectives to guide all incident actions' },
        { key: 'adaptive_organization', label: 'Adaptive, Scalable Organization', type: 'textarea', required: true, placeholder: 'describe or explain how this objective is adaptive and scalable to the incident keeping in mind Modularity, Unified Command, and Span of Control' }
      ]
    },
    'strategies-tactics': {
      label: 'Strategies & Tactics',
      icon: Flag,
      fields: [
        { key: 'strategies_for_objectives', label: 'Strategies for Achieving Objectives', type: 'textarea', required: true, placeholder: 'enter those that establish clear prioritized objectives, that can maintain a manageable span of control, ensure clear communications, and or coordinate with multiple agencies' },
        { key: 'tactical_approaches', label: 'Tactical Approaches and Implementation', type: 'textarea', required: true, placeholder: 'enter those concrete actions and steps taken by the Operations Section to implement the strategic objectives set by the Incident Commander' }
      ]
    },
    'organizational-principles': {
      label: 'Organizational Principles',
      icon: Building,
      fields: [
        { key: 'standardized_approach', label: 'Standardized Approach and Common Terminology', type: 'textarea', required: true, placeholder: 'enter standardized, modular approaches and or terminologies to manage incidents, events, and emergencies of all types and complexities' },
        { key: 'roles_responsibilities', label: 'Clearly Defined Roles and Responsibilities', type: 'textarea', required: true, placeholder: 'enter additional roles or responsibilities that are otherwise outlined in the organization chart' },
        { key: 'span_of_control', label: 'Managed Span of Control (3-7 Subordinates)', type: 'textarea', required: false, placeholder: 'enter those factors that may affect the span of control such as type and complexity of incident, nature of task, hazards/safety, resources, personnel skill sets' },
        { key: 'modular_organization', label: 'Modular Organization Structure', type: 'textarea', required: false, placeholder: 'enter those factors that may affect the flexible and scalable framework that may expand and contract based on the specific objectives, size, and complexity of an incident' }
      ]
    },
    'resource-management': {
      label: 'Resource Management',
      icon: Package,
      fields: [
        { key: 'resource_integration', label: 'Integration of Resources', type: 'textarea', required: true, placeholder: 'enter those resource objectives, mobilizations, and categorizations such personnel, equipment, supplies or facilities that are to be managed and organized into standard, modular structures.  Also refer to the Resource Database located in the Emergency Management Sidebar' },
        { key: 'resource_deployment', label: 'Resource Deployment Strategies', type: 'textarea', required: true, placeholder: 'enter those resource deployment strategies that are directly tied to an incident\'s established objectives using a systematic approach to acquiring, assigning, and tracking all personnel and equipment' }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load objectives data on component mount
  useEffect(() => {
    loadObjectivesData();
  }, [exerciseId]);

  const loadObjectivesData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/incident-objectives?exercise_id=${exerciseId}`);
      setObjectivesData(response.data);
    } catch (error) {
      console.error('Error loading incident objectives data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      timestamp: new Date().toISOString().slice(0, 16),
      location: '',
      contact_info: '',
      contact_name: '',
      contact_phone: '',
      notes: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];

    if (!formData.title) newErrors.title = 'Title is required';
    if (!formData.description) newErrors.description = 'Description is required';

    // Validate category-specific required fields
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/incident-objectives/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/incident-objectives`, entryData);
      }

      resetForm();
      setShowForm(false);
      loadObjectivesData();
    } catch (error) {
      console.error('Error saving incident objective entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'core-objectives',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'active',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      location: entry.location || '',
      contact_info: entry.contact_info || '',
      notes: entry.notes || '',
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      setLoading(true);
      const token = secureStorage.getToken();
      await axios.delete(`${API}/incident-objectives/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      await loadObjectivesData();
      alert('Entry deleted successfully');
    } catch (error) {
      console.error('Error deleting incident objective entry:', error);
      if (error.response?.status === 403) {
        alert('You do not have permission to delete this entry. Only Agency Admins and Super Admins can delete entries.');
      } else if (error.response?.status === 404) {
        alert('Entry not found. It may have already been deleted.');
      } else {
        alert('Failed to delete entry. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (entry = null) => {
    const printContent = entry ? 
      generateSingleEntryPrint(entry) : 
      generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Incident Objectives Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f59e0b; padding-bottom: 15px; }
            .header h1 { color: #f59e0b; margin-bottom: 5px; }
            .header h2 { color: #666; margin-bottom: 10px; }
            .section { margin-bottom: 40px; page-break-inside: avoid; }
            .section h2 { color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 5px; margin-bottom: 20px; }
            .entry { margin-bottom: 25px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; background: #fafafa; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 180px; color: #333; }
            .category { background: #f59e0b; color: white; padding: 10px; margin-bottom: 15px; border-radius: 5px; }
            .category h3 { margin: 0; color: white; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #666; border-top: 2px solid #ddd; padding-top: 15px; }
            @media print { 
              .entry { page-break-inside: avoid; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p><strong>Incident Objectives Report</strong></p>
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleEntryPrint = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="header">
        <h1>Incident Objectives Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
      </div>
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
        <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
        ${category?.fields.map(field => 
          `<div class="field"><span class="label">${field.label}:</span> ${entry[field.key] || 'N/A'}</div>`
        ).join('') || ''}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    const selectedData = selectedEntries.length > 0 ? 
      objectivesData.filter(entry => selectedEntries.includes(entry.id)) : 
      objectivesData;
    
    return `
      <div class="header">
        <h1>Incident Objectives Report</h1>
        <h3>Exercise: ${exerciseName || 'Exercise Report'}</h3>
        <p>${selectedData.length} entry(ies)</p>
      </div>
      ${selectedData.map(entry => generateSingleEntryPrint(entry)).join('')}
    `;
  };

  const toggleEntrySelection = (id) => {
    setSelectedEntries(prev => 
      prev.includes(id) 
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
  };

  const filteredData = activeCategory === 'all' 
    ? (objectivesData || []) 
    : (objectivesData || []).filter(entry => entry.category === activeCategory);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Incident Objectives
            </h1>
            <p className="text-white text-sm">
              Define objectives, strategies, and tactics for incident management and response coordination
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => handlePrint()}
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Objectives
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Objective
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Target className={`h-4 w-4 mr-2 ${
              activeCategory === 'all' ? 'text-white' : 'text-blue-600'
            }`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* Entry Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Objective' : 'Add New Objective'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <form onSubmit={handleSubmitLocal} className="space-y-6">
            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Title of this entry"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.title ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>

            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Emergency Phase *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Removed Incident Name and Timestamp fields */}

            {/* Operational Period: Date/Time From and Date/Time To */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Operational Period - Date / Time from:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_from ? formData.date_from.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_from ? formData.date_from.split('T')[1] : '00:00';
                      setFormData({...formData, date_from: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_from ? formData.date_from.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_from ? formData.date_from.split('T')[0] : '';
                        setFormData({...formData, date_from: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Date / Time to:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={formData.date_to ? formData.date_to.split('T')[0] : ''}
                    onChange={(e) => {
                      const date = e.target.value;
                      const time = formData.date_to ? formData.date_to.split('T')[1] : '00:00';
                      setFormData({...formData, date_to: `${date}T${time}`});
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                  <input
                    type="text"
                    placeholder="HH:MM"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength="5"
                    value={formData.date_to ? formData.date_to.split('T')[1] : ''}
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9:]/g, '');
                      if (value.length === 2 && !value.includes(':')) {
                        value = value + ':';
                      }
                      if (value.length <= 5) {
                        const date = formData.date_to ? formData.date_to.split('T')[0] : '';
                        setFormData({...formData, date_to: `${date}T${value}`});
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Situation Summary *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                placeholder="a collective description of the current situation"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                  errors.description ? 'border-red-500' : 'border-gray-600'
                }`}
              />
              {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            </div>

            {/* Priority and Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Name and Contact Phone */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Name
                </label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  placeholder="First and last name"
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">
                  Contact Phone
                </label>
                <input
                  type="text"
                  value={formData.contact_phone}
                  onChange={(e) => {
                    // Only allow digits and auto-format as XXX-XXX-XXXX
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                    let formatted = '';
                    if (digits.length > 0) {
                      formatted = digits.slice(0, 3);
                      if (digits.length > 3) {
                        formatted += '-' + digits.slice(3, 6);
                      }
                      if (digits.length > 6) {
                        formatted += '-' + digits.slice(6, 10);
                      }
                    }
                    if (formatted.length <= 12) {
                      setFormData({...formData, contact_phone: formatted});
                    }
                  }}
                  placeholder="204-123-4567"
                  maxLength="12"
                  pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                    errors.contact_phone ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.contact_phone && <p className="text-red-500 text-xs mt-1">{errors.contact_phone}</p>}
              </div>
            </div>

            {/* Category-specific fields */}
            {categories[formData.category]?.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-white mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    rows={3}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                ) : (
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    placeholder={field.placeholder || ''}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white ${
                      errors[field.key] ? 'border-red-500' : 'border-gray-600'
                    }`}
                  />
                )}
                {errors[field.key] && <p className="text-red-500 text-xs mt-1">{errors[field.key]}</p>}
              </div>
            ))}

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="notes / comments / remarks"
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Objective' : 'Add Objective')}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Entries List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all' ? 'All Objectives' : categories[activeCategory]?.label + ' Entries'}
          </h3>
        </div>
        
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
              <p className="text-white mt-2">Loading objectives...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-8">
              <Target className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-orange-500 mb-2">
                {activeCategory === 'all' ? 'No Objectives Found' : `No ${categories[activeCategory]?.label} Entries`}
              </h3>
              <p className="text-white mb-4">
                {activeCategory === 'all' 
                  ? 'Start by adding your first incident objective in any category for this exercise.'
                  : `Start by adding your first ${categories[activeCategory]?.label.toLowerCase()} entry for this exercise.`
                }
              </p>
            </div>
          ) : (
            <SortableEntryList
              entries={filteredData}
              setEntries={(newEntries) => {
                if (activeCategory === 'all') {
                  setObjectivesData(newEntries);
                } else {
                  const otherEntries = objectivesData.filter(d => d.category !== activeCategory);
                  setObjectivesData([...otherEntries, ...newEntries]);
                }
              }}
              reorderEndpoint="incident-objectives/reorder"
              renderEntry={(entry) => {
                const priorityOption = priorityOptions.find(p => p.value === entry.priority);
                const statusOption = statusOptions.find(s => s.value === entry.status);
                
                return (
                  <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <Checkbox
                            checked={selectedEntries.includes(entry.id)}
                            onCheckedChange={() => toggleEntrySelection(entry.id)}
                          />
                          <h4 className="text-lg font-semibold text-white">{entry.title}</h4>
                          <Badge className={`${priorityOption?.color} bg-transparent border`}>
                            {priorityOption?.label}
                          </Badge>
                          <Badge className={`${statusOption?.color} bg-transparent border`}>
                            {statusOption?.label}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-300 mb-3">{entry.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-400">
                          {entry.timestamp && (
                            <div>
                              <span className="font-medium">Timestamp:</span> {format24HourTimestamp(entry.timestamp)}
                            </div>
                          )}
                          {entry.location && (
                            <div>
                              <span className="font-medium">Location:</span> {entry.location}
                            </div>
                          )}
                          {entry.contact_info && (
                            <div>
                              <span className="font-medium">Contact:</span> {entry.contact_info}
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-gray-600 rounded text-sm text-gray-300">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handlePrint(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(entry)}
                          variant="ghost"
                          size="sm"
                          className="text-orange-400 hover:text-orange-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(entry.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Safety Module Component

export default IncidentObjectivesModule;
